<template>
  <div class="chemsnap-modal chemtracker-module">
    <header class="chemsnap-header">
      <div class="header-title">
        <h1>ChemSnap AI</h1>
        <div class="beta-tag"><p>Beta</p></div>
      </div>
      <div class="header-close">
        <button
          type="button"
          @click.passive="$emit('close-modal')"
          aria-label="close modal"
        >
          <img :src="closeIconSrc" alt="close button" />
        </button>
      </div>
    </header>
    <div class="uploading-screen" v-if="isBusy">
      <raft-busy :busy-text="busyText" :is-raft-styling="false" />
      <div class="chemsnap-button">
        <v-button
          class="ghost destructive small"
          @click="handleCancel"
          aria-label="cancel upload"
          >Cancel</v-button
        >
      </div>
    </div>
    <div class="sample-image-screen" v-else>
      <p>
        Take or upload a photo of your container with the label prominently
        shown.
      </p>
      <h2>Example</h2>
      <p>
        <img :src="exampleSrc" alt="example container with label shown" />
      </p>
      <div class="chemsnap-button">
        <v-button
          class="primary small"
          @click="onUploadFile"
          aria-label="upload"
          >Upload</v-button
        >
        <input
          type="file"
          accept="image/*"
          ref="fileInput"
          @change="onFilePicked"
          hidden
        />
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import chemSnapExample from "../../images/chemsnap-example.png";
import VButton from "../raft/VButton.vue";
import RaftBusy from "../RaftBusy.vue";
import closeIcon from "../../images/closeIcon.svg";
import ErrorStateLinks from "../raft/ErrorStateLinks.vue";
import { useImageProcessing } from "../../composables/useImageProcessing";

export default {
  name: "ChemSnapUpload",
  setup() {
    const { needToResize, resizeToFile } = useImageProcessing();
    return {
      needToResize,
      resizeToFile,
    };
  },
  data() {
    return {
      exampleSrc: chemSnapExample,
      closeIconSrc: closeIcon,
      isBusy: false,
      originalFile: null,
      busyText: "",
      finalFile: null,
    };
  },
  components: {
    VButton,
    RaftBusy,
  },
  props: {
    chemSnapConfig: {
      type: Object,
      required: true,
    },
  },
  computed: {
    fileName() {
      return this.originalFile ? this.originalFile.name : "";
    },
    formData() {
      const formData = new FormData();
      formData.append("org", this.chemSnapConfig.org_id);
      formData.append("api_key", this.chemSnapConfig.key);
      formData.append("file", this.finalFile);
      return formData;
    },
    endpoint() {
      return `${this.chemSnapConfig?.endpoint}/${this.chemSnapConfig?.org_id}/image_upload_b64`;
    },
  },
  methods: {
    onUploadFile() {
      this.$refs.fileInput.click();
    },
    emitSuccess(response) {
      return this.$emit("upload-success", {
        data: response.data?.data?.post,
        uploadedImage: this.finalFile,
      });
    },
    showErrorNotifications(text = null, showLinks = true) {
      if (text) this.$showNotification("error", text);
      if (showLinks) this.$showNotification("error", ErrorStateLinks);
    },
    async onFilePicked(event) {
      this.isBusy = true;

      const file = event.target.files[0];
      this.originalFile = file;
      this.busyText = `Uploading ${this.fileName}...`;

      // Limit to image types that API can handle.
      const allowedTypes = [
        "image/webp",
        "image/jpeg",
        "image/png",
        "image/gif",
      ];
      if (!allowedTypes.includes(file.type)) {
        this.isBusy = false;
        this.showErrorNotifications(
          "Please upload an image in WEBP, JPEG, PNG, or GIF format.",
          false,
        );
        return;
      }

      let payload = file; // If size is <500kb, send original file to AI
      if (this.needToResize(file)) {
        // If size is too large, resize then send to AI
        try {
          payload = await this.resizeToFile(file);
        } catch (error) {
          this.isBusy = false;
          this.showErrorNotifications("Error resizing image." + error);
        }
      }
      await this.handleSendToAI(payload).catch(() => {
        this.isBusy = false;
        this.showErrorNotifications("Error uploading to ChemSnap AI.");
      });
    },
    async handleSendToAI(finalFileToSend) {
      this.finalFile = finalFileToSend;
      this.busyText = `Sending ${this.fileName} to AI...`;

      const response = await axios
        .post(this.endpoint, this.formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        })
        .catch((error) => {
          this.showErrorNotifications(`${error}`);
        });
      if (response) {
        const postResponse = response.data?.data?.post;
        const postStatus = response.data?.status;
        if (postStatus >= 200 && postStatus < 300) {
          // Strip any junk results.
          Object.entries(postResponse).forEach(([key, value]) => {
            if (
              value === "null" ||
              value === null ||
              value.match(
                /can't assist with extracting or interpreting text from images/g,
              )
            ) {
              postResponse[key] = "";
            }
          });
          const missingMinimumInfo = ["CAS number", "Chemical name"].every(
            (field) => postResponse[field] === "",
          );

          if (missingMinimumInfo) {
            this.$showNotification(
              "info",
              "Unable to find CAS Number or Chemical Name. Fill out the form manually or try a different image.",
            );
          } else {
            this.$showNotification(
              "success",
              "Image scanned by AI successfully.",
            );
          }

          // Notify the parent with the response data.
          this.emitSuccess(response);
        }
        if (postStatus === "500") {
          this.showErrorNotifications(postResponse);
        }
      }
      this.isBusy = false;
      this.handleCancel();
    },
    handleCancel() {
      this.$emit("close-modal");
    },
  },
};
</script>

<style scoped>
.chemsnap-modal {
  padding: 0.75rem;
}
header.chemsnap-header {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
}
header.chemsnap-header > div.header-title {
  display: flex;
  flex-direction: row;
  gap: 0.5rem;
  align-items: center;
}
header.chemsnap-header > div.header-close > button {
  cursor: pointer;
  background: none;
  color: inherit;
  border: none;
  padding: 0;
  outline: inherit;
}
.beta-tag {
  background-color: var(--blue-100);
  border-radius: 0.3rem;
}
.beta-tag > p {
  padding: 0.1rem 0.25rem;
  margin: 0;
}
.sample-image-screen > p {
  font-size: var(--font-size-75);
}
.chemsnap-button {
  display: flex;
  justify-content: end;
}
</style>
